import SubtleBadge from "components/common/SubtleBadge";
import { useUserManagement } from "hooks";
import useDonationsPurposes from "hooks/donations/useDonationPurposes";
import useDonationsList from "hooks/donations/useDonationsList";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import selectStyles, { multiSelectStyles } from "./selectStyles";


export const IssueCreditPage = () => {
  const { donationId } = useParams();
  const navigate = useNavigate();
  const { loading, fetchDonationById } = useDonationsList();
  const [donation, setDonation] = useState(null);
  const { users, loading: usersLoading } = useUserManagement();
  const { donationPurposes: allPurposes, loading: purposesLoading } =
    useDonationsPurposes();

  // Form fields
  const [selectedUser, setSelectedUser] = useState(null);
  const [amount, setAmount] = useState(0);
  const [expiresAt, setExpiresAt] = useState("");
  const [selectedPurposes, setSelectedPurposes] = useState([]);

  const [amountError, setAmountError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchDonation = async () => {
      if (!donationId) return;
      try {
        const donation = await fetchDonationById(donationId);
        setDonation(donation);

        // Preselect purposes belonging to this donation
        if (donation?.purposes?.length) {
          setSelectedPurposes(
            donation.purposes.map((p) => ({
              value: p.id,
              label: p.name,
            }))
          );
        }
      } catch (err) {
        console.error("Error fetching donation:", err);
        setDonation(null);
      }
    };
    fetchDonation();
  }, [donationId, fetchDonationById]);

  // Async loader for users
  const loadUsers = async (inputValue) => {
    const filtered = users.filter((u) =>
      `${u.firstName} ${u.lastName} ${u.email}`
        .toLowerCase()
        .includes(inputValue.toLowerCase())
    );

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(
          filtered.map((u) => ({
            value: u.id,
            label: `${u.firstName} ${u.lastName} (${u.email})`,
          }))
        );
      }, 1000);
    });
  };

  // Async loader for purposes
  const loadPurposes = async (inputValue) => {
    if (!inputValue || inputValue.trim() === "") {
      // return all purposes when no input
      return allPurposes?.map((p) => ({
        value: p.id,
        label: p.name,
      }));
    }

    // filter based on search input
    const filtered = allPurposes?.filter((p) =>
      p.name.toLowerCase().includes(inputValue.toLowerCase())
    );

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(
          filtered.map((p) => ({
            value: p.id,
            label: p.name,
          }))
        );
      }, 1000);
    });
  };

  const statusMap = {
    ACTIVE: { type: "success", label: "Active" },
    PENDING: { type: "warning", label: "Pending" },
    EXPIRED: { type: "danger", label: "Expired" },
    DELETED: { type: "danger", label: "Deleted" },
  };

  const { type, label } = statusMap[donation?.status] || {
    type: "secondary",
    label: "Unknown",
  };

  const handleAmountChange = (e) => {
    const val = parseFloat(e.target.value);
    setAmount(val);

    const balance = Number(donation?.balance || 0);

    if (val > balance) {
      setAmountError(
        `Amount exceeds available balance of $${balance.toFixed(2)}.`
      );
    } else if (val <= 0) {
      setAmountError("Amount must be greater than 0.");
    } else {
      setAmountError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // basic checks
    if (!selectedUser) {
      toast.error("Select a user to issue credit.");
      return;
    }

    // ensure amount is a number
    const parsedAmount = Number(amount);
    if (!parsedAmount || Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      toast.error("Enter a valid amount greater than 0.");
      return;
    }

    // expiry check
    if (!expiresAt) {
      toast.error("Please select an expiry date.");
      return;
    }

    // amount must not exceed donation balance
    const balance = Number(donation?.balance || 0);
    if (parsedAmount > balance) {
      toast.error(
        `Amount cannot exceed the donation balance of $${balance.toFixed(2)}.`
      );
      return;
    }

    // purposes check (selectedPurposes is AsyncSelect value array)
    if (!selectedPurposes || selectedPurposes.length === 0) {
      toast.error("Select at least one purpose.");
      return;
    }

    const payload = {
      userId: selectedUser?.value,
      unit: "MONEY",
      amount: parseFloat(parsedAmount.toFixed(2)),
      currency: donation?.currency || "USD",
      expiresAt: new Date(expiresAt).toISOString(),
      status: "ACTIVE",
      donationId: donationId,
      purposeIds: selectedPurposes.map((p) => p.value),
    };

    try {
      setSubmitting(true);
      const authToken = sessionStorage.getItem("authToken");

      const response = await apiClient.post(
        "https://dev.oliveeb.com/olitask-service/api/v1/credits",
        payload,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      console.log("Response:", response.data);

      toast.success("Credit issued successfully!");
      // redirect back to credits list
      navigate("/olitask/donations/credits");
    } catch (err) {
      let message = "An unexpected error occurred.";
      if (err.response) {
        message =
          err.response.data?.message ||
          `Failed to issue credit: ${err.response.statusText}`;
      } else if (err.request) {
        message =
          "No response from the server. Please check your internet connection.";
      } else {
        message = err.message || "Something went wrong.";
      }
      toast.error(message);
      console.error("Error issuing credit:", err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !donation) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <>
      <Card className="mb-4 shadow-none mt-3 rounded border-1">
        <Card.Body className="d-flex align-items-center py-3 px-4">
          {/* Left side: Back button */}
          <div className="d-flex align-items-center" style={{ minWidth: 90 }}>
            <Button
              type="button"
              onClick={() => navigate("/olitask/donations/list")}
              size="sm"
              aria-label="Go back"
              variant="outline-success"
            >
              <span style={{ fontSize: "1.3em", lineHeight: 1 }}>←</span> Back
            </Button>
          </div>

          {/* Center: Title */}
          <div className="px-3">
            <h4 className="mb-0 fw-semibold text-base">
              Issue Credit from Donation
            </h4>
          </div>
        </Card.Body>
      </Card>

      <Row className="mt-3">
        <Col md={4} className="mb-4">
          <Card className="rounded-4">
            <Card.Header>
              <h6 className="mb-0 fw-bold">Donation details</h6>
            </Card.Header>
            <Card.Body className="py-0">
              <Row>
                <Col>
                  <div className="mb-3">
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Donation ID
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.id.slice(0, 25)}...
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Donor</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.donor?.firstName} {donation?.donor?.lastName}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Code</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.code}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">Amount</label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        ${donation?.amount ? donation?.amount.toFixed(2) : 0.0}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Balance
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        $
                        {donation?.balance
                          ? donation?.balance.toFixed(2)
                          : "0.00"}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Currency
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.currency}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <label className="form-label text-xxs mb-0">
                        Short Note
                      </label>
                      <h6 className="mb-0 fw-semibold text-sm">
                        {donation?.note || "Not provided"}
                      </h6>
                    </div>
                    <div className="mb-2">
                      <div className="mb-2">
                        <label className="form-label text-xxs mb-0 d-block">
                          Status
                        </label>
                        <SubtleBadge pill bg={type}>
                          {label}
                        </SubtleBadge>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <h5 className="mb-3">Issue credit</h5>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>User</Form.Label>
              <AsyncSelect
                cacheOptions
                defaultOptions={users.map((u) => ({
                  value: u.id,
                  label: `${u.firstName} ${u.lastName} (${u.email})`,
                }))}
                loadOptions={loadUsers}
                onChange={(opt) => setSelectedUser(opt)}
                value={selectedUser}
                placeholder="Search and select user..."
                isClearable
                isLoading={usersLoading}
                styles={selectStyles}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <Form.Control
                type="number"
                min="0.01"
                step="0.01"
                value={amount}
                onChange={handleAmountChange}
                isInvalid={!!amountError}
                placeholder="0.01"
              />
              <Form.Control.Feedback type="invalid">
                {amountError}
              </Form.Control.Feedback>
              {!amountError && (
                <small>
                  Available balance: ${donation?.balance?.toFixed(2)}
                </small>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Expiry Date</Form.Label>
              <Form.Control
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>Purposes</Form.Label>

              <AsyncSelect
                isMulti
                cacheOptions
                defaultOptions={allPurposes?.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                loadOptions={loadPurposes}
                value={selectedPurposes}
                onChange={(options) => setSelectedPurposes(options)}
                placeholder="Select or search purposes..."
                isLoading={purposesLoading}
                styles={multiSelectStyles}
              />
            </Form.Group>

            {submitting ? (
              <Spinner animation="border" variant="success" />
            ) : (
              <Button
                type="submit"
                variant="success"
                disabled={!selectedUser || !amount}
                className="text-sm"
              >
                Issue credit
              </Button>
            )}
          </Form>
        </Col>
      </Row>
    </>
  );
};
